import { hex, lighten, mixColor, type Color } from "../../common/color.js";
import { Control, type ControlOptions } from "../core/Control.js";
import type { Desktop } from "../core/Desktop.js";
import type { Point } from "../../common/geometry.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme, ThemePalette } from "../themes/Theme.js";

/**
 * A color as an art control takes it: a `Color`, a palette slot's name (`"accent"`), or a hex
 * string (`"#ff6f42"`).
 *
 * The name is the point. It is resolved against the theme every frame rather than once, so an
 * ember field written as `accent` burns in whatever the current theme's accent is - the same
 * reason page documents store palette tokens and never literals.
 */
export type ColorRef = Color | string;

/** Resolve a `ColorRef` against a palette, or null when it names nothing. */
export function resolveColorRef(ref: ColorRef | null | undefined, palette: ThemePalette): Color | null {
  if (ref === null || ref === undefined) return null;
  if (typeof ref !== "string") return ref;
  const s = ref.trim();
  if (s.startsWith("#")) {
    try {
      return hex(s);
    } catch {
      return null;
    }
  }
  return (palette as unknown as Record<string, Color | undefined>)[s] ?? null;
}

/** The hot core of a fire: its color pushed toward white, for sparks and leading edges. */
export function sparkOf(fire: Color): Color {
  return mixColor(lighten(fire, 0.35), { r: 1, g: 0.95, b: 0.8, a: fire.a }, 0.35);
}

export interface ArtOptions extends ControlOptions {
  /** Multiplies the art's own clock. 0 freezes it. */
  speed?: number;
  /** Picks a different, but still fixed, scatter of stars, embers, cracks or runes. */
  seed?: number;
  /** Whether it moves at all. False paints one settled frame. */
  animate?: boolean;
}

/**
 * The base of the painted art that scenes are built from: skies, horizons, globes, rings.
 *
 * Three rules, and they are the difference between art and a control:
 *
 * - **It never takes the pointer.** A field of embers over a button must not swallow its click,
 *   and the scene's art bands relying on that would be a rule with an exception.
 * - **It paints from a clock and a seed, nothing else.** Every frame is a pure function of the
 *   seconds since the art first appeared, so there is no state to drift and a frame can be
 *   tested by asking for it.
 * - **It honors reduced motion** by freezing at `settledTime` - late enough that anything which
 *   draws itself in has finished, so the still frame is the finished picture.
 *
 * It keeps itself registered as a desktop animator while it is attached and moving; the desktop
 * already ignores an animator it cannot see, so a hidden sky costs nothing.
 */
export abstract class ArtControl extends Control {
  speed: number;
  seed: number;
  #animate: boolean;
  #started = -1;
  #registeredWith: Desktop | null = null;

  constructor(options: ArtOptions = {}) {
    super({ width: 200, height: 200, ...options });
    this.speed = options.speed ?? 1;
    // 0 is the scatter the original screens drew from, so a default starfield or globe is theirs.
    this.seed = options.seed ?? 0;
    this.#animate = options.animate ?? true;
  }

  get animate(): boolean {
    return this.#animate;
  }
  set animate(v: boolean) {
    this.#animate = v;
    this.#syncAnimator();
    this.invalidate();
  }

  /** Seconds a still frame is taken at: after every draw-in has finished. */
  protected get settledTime(): number {
    return 6;
  }

  /** Seconds of art clock since this first painted, scaled by `speed`. */
  get time(): number {
    const desktop = this.desktop;
    if (!this.#animate || desktop?.reducedMotion) return this.settledTime;
    const now = desktop?.frameTime ?? 0;
    if (this.#started < 0) this.#started = now;
    return (now - this.#started) * this.speed;
  }

  /** Start the clock again from zero, so anything that draws itself in does so again. */
  restart(): void {
    this.#started = -1;
    this.invalidate();
  }

  protected override hitTestSelf(_p: Point): boolean {
    return false;
  }

  protected override onEnvironmentChanged(): void {
    this.#syncAnimator();
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    this.#syncAnimator();
    this.paintArt(rc, theme, this.time);
  }

  /** Draw one frame at `t` seconds, inside (0, 0, width, height). */
  protected abstract paintArt(rc: RenderContext, theme: Theme, t: number): void;

  /** Resolve a color option, falling back when it names nothing. */
  protected ink(ref: ColorRef | null | undefined, theme: Theme, fallback: ColorRef): Color {
    return (
      resolveColorRef(ref, theme.palette) ??
      resolveColorRef(fallback, theme.palette) ??
      theme.palette.accent
    );
  }

  /** Whether anything it draws moves with the clock. A still drawing need not keep the desktop busy. */
  protected get moves(): boolean {
    return true;
  }

  /** Re-check whether the clock should keep this painting, after something `moves` reads changed. */
  protected syncClock(): void {
    this.#syncAnimator();
  }

  #syncAnimator(): void {
    const desktop = this.desktop;
    const want = desktop !== null && this.#animate && !desktop.reducedMotion && this.speed !== 0 && this.moves;
    const target = want ? desktop : null;
    if (target === this.#registeredWith) return;
    this.#registeredWith?.removeAnimator(this);
    this.#registeredWith = target;
    target?.addAnimator(this);
  }
}
